import { createFileRoute, notFound } from "@tanstack/react-router";
import { useEffect } from "react";
import { SeoPageView } from "@/components/seo-page-view";
import { countryFromSlug, airportsByCountry, findAirport, mainDestinationInCountry } from "@/lib/airports";
import { countryGen } from "@/lib/seo-routes";
import { loadSeoPage, buildSeoPageHead, triggerSeoGeneration } from "@/lib/seo-page-loader";
import { buildSearchHref } from "@/lib/search-href";
import { getInitialOrigin } from "@/lib/initial-origin.functions";
import { breadcrumbScript } from "@/lib/breadcrumbs";
import { faqScript } from "@/lib/faq-schema";
import { setEdgeCache } from "@/lib/ssr-cache";

export const Route = createFileRoute("/loty/kraj/$slug")({
  loader: async ({ params }) => {
    const country = countryFromSlug(params.slug);
    if (!country) throw notFound();
    await setEdgeCache();
    // Równolegle: SEO page, geo-hub usera (SSR, bez migotania) i sugerowany
    // kierunek (pierwsze lotnisko w kraju) — żeby SearchForm na landingu
    // krajowym miał sensowny default `from` (geo CF) i `to` (kraj z URL).
    const [page, origin] = await Promise.all([
      loadSeoPage({
        type: "country", slug: params.slug, label: `Tanie loty do ${countryGen(country)}`,
      }),
      getInitialOrigin({ data: { page: "/loty/kraj" } }),
    ]);
    const today = new Date(); today.setUTCHours(0,0,0,0);
    const dd = new Date(today.getTime() + 30 * 86400_000).toISOString().slice(0, 10);
    const rd = new Date(today.getTime() + 37 * 86400_000).toISOString().slice(0, 10);
    // Aviasales nie wyszukuje po krajach — jako cel ustawiamy główne miasto
    // kraju (np. Wielka Brytania → Londyn, wszystkie lotniska).
    const defaultTo = mainDestinationInCountry(country) ?? airportsByCountry(country)[0]?.code ?? null;
    const fromName = findAirport(origin.from)?.city;
    return { slug: params.slug, country, page, dd, rd, initialFrom: origin.from, fromName, defaultTo };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return { meta: [] };
    const { slug, country, page } = loaderData;
    const path = `/loty/kraj/${slug}`;
    const head = buildSeoPageHead(
      page,
      path,
      `Tanie loty do ${countryGen(country)} | Tanie Latanie`,
      `Najtańsze bilety lotnicze z Polski do ${countryGen(country)}. Porównaj ceny i znajdź najlepszy termin.`
    );
    const breadcrumb = breadcrumbScript([
      { name: "Strona główna", path: "/" },
      { name: "Loty", path: "/loty" },
      { name: `Loty do ${countryGen(country)}`, path },
    ]);
    const faqLd = page?.faq ? faqScript(page.faq) : null;
    const scripts = [...(head.scripts ?? []), breadcrumb];
    if (faqLd) scripts.push(faqLd);
    return { ...head, scripts };
  },
  component: CountryPage,
});

function CountryPage() {
  const { slug, country, page, dd, rd, initialFrom, fromName, defaultTo } = Route.useLoaderData();
  useEffect(() => {
    if (!page) triggerSeoGeneration({ type: "country", slug, label: `Tanie loty do ${countryGen(country)}` });
  }, [page, slug, country]);
  return (
    <SeoPageView
      type="country"
      from={initialFrom}
      fromName={fromName}
      defaultTo={defaultTo ?? undefined}
      dd={dd}
      rd={rd}
      adults={1}
      page={page}
      countryName={country}
      fallbackH1={`Tanie loty do ${countryGen(country)}`}
      searchHref={buildSearchHref({
        from: initialFrom,
        to: defaultTo ?? "BCN",
        dd, rd, adults: 1,
      })}
    />
  );
}
